import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
// import OktaLogo from '../../assets/icons/okta.svg';
// import allAgent from '../../assets/images/allAgentBg.webp';
import CustomTextField from "../CustomTextfield/index";
import CustomCheckbox from "../CustomCheckbox/index";
import { useNavigate } from "react-router-dom";
import "./LoginPage.scss";
import { encryptPayload } from "../../utility/helpers";
import { loginUser } from "../../redux/slices/authSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const REMEMBER_ME_KEY = "rememberedEmail";

export default function StaticLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // validation state
  const [emailError, setEmailError] = useState(false);
  const [emailHelper, setEmailHelper] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordHelper, setPasswordHelper] = useState("");
  const emailRegex = /^\S+@\S+\.\S+$/;

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_ME_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const validate = () => {
    let valid = true;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError(true);
      setEmailHelper("Please enter your email");
      valid = false;
    } else {
      if (!emailRegex.test(trimmedEmail)) {
        setEmailError(true);
        setEmailHelper("Please enter a valid email address");
        valid = false;
      } else {
        setEmailError(false);
        setEmailHelper("");
      }
    }
    if (!password.trim()) {
      setPasswordError(true);
      setPasswordHelper("Please enter your password");
      valid = false;
    } else {
      setPasswordError(false);
      setPasswordHelper("");
    }

    return valid;
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const el = e.target;

    if (el.tagName === "BUTTON") return;
    handleLogin();
  };

  const handleLogin = () => {
    const isValid = validate();
    if (!isValid) return;

    if (isLoggingIn) return;
    setIsLoggingIn(true);

    const trimmedEmail = email.trim();
    const payload = { email: trimmedEmail, password };
    const encryptPayload1 = encryptPayload(payload);

    dispatch(loginUser({ encryptPayload1, navigate }))
      .unwrap()
      .then(() => {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, trimmedEmail);
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
        }
        toast.success("Login Successful !");
      })
      .catch((error) => {
        if (error?.data?.detail === "Incorrect username or password") {
          toast.error("Invalid Credentials !");
        } else {
          toast.error("Login failed");
        }
      })
      .finally(() => {
        setIsLoggingIn(false);
      });
  };

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError(false);
      setEmailHelper("");
    }
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
    if (passwordError) {
      setPasswordError(false);
      setPasswordHelper("");
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
      }}
    >
      {/* LEFT SIDE */}
      <Box
        sx={{
          width: { xs: "100%", md: "100%" },
          padding: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack gap={5} className="login-form-container">
          <Box>
            <Box className="login-heading-container">
              <Typography id="login-heading" variant="h4">
                Login
              </Typography>
              <Typography id="subHeader">
                Login to access your account
              </Typography>
            </Box>

            {emailError || passwordError ? (
              <Typography color="error" mb={2}>
                Username or password is incorrect
              </Typography>
            ) : (
              <></>
            )}
            <Stack direction="column" gap={4}>
              <FormControl className="login-email-container">
                <div className="email-label">Email</div>
                <CustomTextField
                  placeholder="Enter Email"
                  autoComplete="on"
                  className="login-form-email"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={onEmailChange}
                  error={emailError}
                  helperText={emailHelper}
                />
              </FormControl>

              <FormControl className="pwd-container">
                <div className="email-label">Password</div>
                <CustomTextField
                  placeholder="Enter Password"
                  onKeyDown={handleKeyDown}
                  className="form-pwd"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={onPasswordChange}
                  error={passwordError}
                  helperText={passwordHelper}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </FormControl>

              <Box className="rememberMeContainer">
                <FormControlLabel
                  control={
                    <CustomCheckbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me"
                />
              </Box>

              <Button
                size="small"
                variant="contained"
                fullWidth
                id="login-submit-button"
                onClick={handleLogin}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? "Logging in..." : "Login"}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
